import { Alert, Button } from "@mui/material";
import { DisplayableError } from "./DisplayableError";
import { type FallbackUI } from "./ErrorBoundaryComponent";
import { BodyTextM } from "../Typography";
import { cs } from "../../translations";
import * as Styled from "./DefaultErrorUi.styled";

interface ErrorMessageProps {
    title?: string;
    message?: string;
    errorMessage?: string;
    buttonText?: string;
    customButton?: JSX.Element | null;
}

const ErrorMessage = ({
    title = cs.errors.somethingWentWrong,
    message = cs.errors.general,
    buttonText = cs.errors.tryAgain,
    errorMessage,
    customButton,
}: ErrorMessageProps) => {
    const button = (() => {
        if (customButton === null) return null;
        return (
            customButton ?? (
                <Button onClick={() => { window.location.reload(); }} variant="contained">
                    {buttonText}
                </Button>
            )
        );
    })();

    return (
        <>
            <Alert severity="error">
                <Styled.ErrorTitle strong>
                    {title}
                </Styled.ErrorTitle>
                <div>
                    <BodyTextM>{message}</BodyTextM>
                </div>
            </Alert>
            {errorMessage && (
                <Styled.ErrorInfoStack>
                    <Styled.ErrorInfoHeading>{cs.errors.moreInfo}</Styled.ErrorInfoHeading>
                    <Styled.ErrorInfoBox>
                        <Styled.ErrorInfoMessage>{errorMessage}</Styled.ErrorInfoMessage>
                    </Styled.ErrorInfoBox>
                </Styled.ErrorInfoStack>
            )}
            {button}
        </>
    );
};

export const DefaultErrorUI: FallbackUI = ({ error }) => {
    if (error instanceof DisplayableError) {
        if ("customJSX" in error.display) {
            return error.display.customJSX;
        }
        return (
            <Styled.ErrorContentStack>
                <ErrorMessage
                    title={error.display.title}
                    message={error.display.message}
                    buttonText={error.display.buttonText}
                    errorMessage={error.message}
                    customButton={error.display.customButton}
                />
            </Styled.ErrorContentStack>
        );
    }

    return (
        <Styled.ErrorContentStack>
            <ErrorMessage errorMessage={error.message} />
        </Styled.ErrorContentStack>
    );
};

export const DefaultErrorUIWithCard: FallbackUI = ({ error }) => (
    <Styled.ErrorCard elevation={4}>
        <DefaultErrorUI error={error} />
    </Styled.ErrorCard>
);
